import MainLayout from "../../../layout/MainLayout";
import {
  Button1,
  Button2,
  Select,
  Checkbox,
  ToggleButton,
} from "../../index";
import plusIcon from "../../../assets/icons/plus.png";
import Table from "../../table/Table";
import calendarIcon from "../../../assets/icons/calendar-icon.png";
import sortIcon from "../../../assets/icons/sort-icon.png";
import listIcon from "../../../assets/icons/list-icon.png";
import { DatePicker, Dropdown, message } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { billsApi } from "../../../api/endpoints";
import { useAccounts } from "../../../data/live";
import { useAuth } from "../../../api/useAuth";

const last4 = (s) => (s ? String(s).slice(-4) : "0000");

const Billers = () => {
  const navigate = useNavigate();
  const { accounts } = useAccounts();
  const { user } = useAuth();

  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All Bills");
  const [sourceAccountId, setSourceAccountId] = useState("");
  const [sortBy, setSortBy] = useState("dueDate-asc");
  const [monthFilter, setMonthFilter] = useState(null);

  const userName = user
    ? `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email
    : "Account";

  const accountOptions = accounts.map((a) => ({
    name: `${a.currency} • ****${last4(a.accountNumber || a.iban)}${
      a.isDefault ? " (Default)" : ""
    }`,
    value: a.id,
  }));

  useEffect(() => {
    if (accounts.length > 0 && !sourceAccountId) {
      setSourceAccountId(accounts[0].id);
    }
  }, [accounts, sourceAccountId]);

  const refresh = () => {
    setLoading(true);
    billsApi
      .listBills()
      .then((data) => setBills(Array.isArray(data) ? data : []))
      .catch(() => setBills([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh();
  }, []);

  const handlePay = async (id) => {
    try {
      await billsApi.pay(id);
      message.success("Bill paid");
      refresh();
    } catch (e) {
      message.error(
        e.response?.data?.error || e.message || "Bill payment failed"
      );
    }
  };

  const filtered = bills
    .filter((b) => {
      if (filter !== "All Bills") {
        if ((b.frequency || "").toLowerCase() !== filter.toLowerCase()) return false;
      }
      if (monthFilter && b.dueDate) {
        const due = new Date(b.dueDate);
        if (
          due.getMonth() !== monthFilter.getMonth() ||
          due.getFullYear() !== monthFilter.getFullYear()
        ) {
          return false;
        }
      }
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "dueDate-asc":
          return new Date(a.dueDate || 0) - new Date(b.dueDate || 0);
        case "dueDate-desc":
          return new Date(b.dueDate || 0) - new Date(a.dueDate || 0);
        case "amount-desc":
          return Number(b.amount || 0) - Number(a.amount || 0);
        case "amount-asc":
          return Number(a.amount || 0) - Number(b.amount || 0);
        case "name-asc":
          return (a.biller?.name || a.billerName || "").localeCompare(
            b.biller?.name || b.billerName || ""
          );
        default:
          return 0;
      }
    });

  const billerTableHeader = [
    <Checkbox key="cb" />,
    "Biller",
    "Category",
    "Frequency",
    "Due Date",
    "Status",
    "Amount",
    "Action",
  ];

  const billerTableBody = filtered.map((b) => ({
    bill: b.biller?.name || b.billerName || "-",
    category: b.category || "-",
    paymentMethod: b.paymentMethod || "Manual Payment",
    frequency: b.frequency || "Once",
    date: b.dueDate
      ? new Date(b.dueDate).toLocaleDateString()
      : "-",
    paid: b.status === "PAID",
    amount: `${Number(b.amount || 0).toLocaleString()} ${b.currency || "AUD"}`,
    actions:
      b.status === "PAID" ? null : (
        <Button1
          className="btn-dark px-3 py-1 fs12px"
          buttonName={"Pay"}
          onClick={() => handlePay(b.id)}
        />
      ),
  }));

  return (
    <>
      <MainLayout NavName="Manage My Spending" expand={false}>
        <div>
          <div className="container mt-5">
            <div className="d-flex flex-column gap-3">
              <div>
                <Select
                  value={sourceAccountId}
                  onChange={(e) => setSourceAccountId(e.target.value)}
                  options={accountOptions}
                  defaultValue="Select account"
                  className="bg-light rounded-5 w-auto"
                  popupClassName="bg-White"
                />
              </div>

              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <span className="fs-5 fw-semibold">My Bills</span>
                <div className="d-flex align-items-center gap-2 flex-wrap ">
                  <Button1
                    className="border"
                    buttonName={"Request New Biller"}
                  />
                  <Button1
                    className="border"
                    buttonName={"Configure Reminder"}
                  />
                  <Button1
                    className="border"
                    buttonName={"View All Billers"}
                  />
                  <Button2
                    className={"btn-outline-success"}
                    icon={<img src={plusIcon} alt="add" />}
                    buttonName={"Add New Biller"}
                    divClassName={"gap-2"}
                    onClick={() => navigate("/add-biller")}
                  />
                </div>
              </div>

              <div className="d-flex flex-column gap-3 border rounded-3">
                <div className="d-flex justify-content-md-between p-3 gap-2 px-5 border-bottom align-items-center flex-wrap justify-content-center">
                  <div style={{ width: "60%" }}>
                    <ToggleButton
                      options={[
                        "All Bills",
                        "Today",
                        "Weekly",
                        "Fortnightly",
                        "Monthly",
                        "Quarterly",
                        "Annually",
                      ]}
                      defaultValue={filter}
                      onChange={setFilter}
                      block={true}
                    />
                  </div>
                  <div className="d-flex gap-2 align-items-center">
                    <Dropdown
                      menu={{
                        items: [
                          { key: "dueDate-asc", label: "Due date, soonest" },
                          { key: "dueDate-desc", label: "Due date, latest" },
                          { key: "amount-desc", label: "Amount, high to low" },
                          { key: "amount-asc", label: "Amount, low to high" },
                          { key: "name-asc", label: "Biller, A to Z" },
                        ],
                        onClick: ({ key }) => setSortBy(key),
                      }}
                      trigger={["click"]}
                      placement="bottomRight"
                    >
                      <div>
                        <Button2
                          buttonName={"Sort"}
                          icon={<img src={sortIcon} alt="sort" />}
                          className={"border"}
                          divClassName={"gap-2"}
                        />
                      </div>
                    </Dropdown>
                    <DatePicker
                      suffixIcon={<img src={calendarIcon} alt="sort" />}
                      className="rounded-pill"
                      placeholder="Select month"
                      picker="month"
                      onChange={(d) => setMonthFilter(d ? d.toDate() : null)}
                    />
                    <ToggleButton
                      defaultValue={"List"}
                      options={[
                        {
                          value: "List",
                          icon: <img src={listIcon} alt="list" />,
                        },
                        {
                          value: "Calendar",
                          icon: (
                            <Link to={"/biller-calendarview"}>
                              <img src={calendarIcon} alt="calendar" />
                            </Link>
                          ),
                        },
                      ]}
                    />
                  </div>
                </div>

                <div className="px-4 p-3">
                  {loading ? (
                    <div className="text-center p-3">Loading bills...</div>
                  ) : bills.length === 0 ? (
                    <div className="text-center p-4 text-muted">
                      No bills yet. Click "Add New Biller" to create one.
                    </div>
                  ) : (
                    <Table
                      BillersTable={true}
                      tableHeaders={billerTableHeader}
                      tableBody={billerTableBody}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </MainLayout>
    </>
  );
};

export default Billers;
